import type { BasicPresenceFragment } from '@drycut/graphql';

import { buttonVariants, Stack } from '@drycut/ui-components';
import { Field, Radio, RadioGroup } from '@headlessui/react';
import { DateTime } from 'luxon';

import type { WeekInfo } from './ScheduleSelect';

import { buildPresenceSlots, extendSlotsToSingleSchedule } from './utils';

type Props = {
  onSelect: (day: DateTime) => void;
  presences: BasicPresenceFragment[];
  selectedDate: DateTime;
  weekInfo: WeekInfo;
};

export default function DaySelect({
  weekInfo,
  selectedDate,
  presences,
  onSelect,
}: Props): React.ReactElement {
  const weekdays = []; // Array to hold all days of the week
  let currentDate = weekInfo.startOfWeek;

  // Build an array with all days for the week
  while (currentDate <= weekInfo.endOfWeek) {
    weekdays.push(currentDate);
    currentDate = currentDate.plus({ days: 1 });
  }

  return (
    <Stack gap={2}>
      <ul className="flex flex-row justify-between items-center">
        {weekdays.map((day) => (
          <li
            key={day.toSQLDate()}
            className="flex w-12 text-center justify-center text-sm text-gray-400"
          >
            {day.setLocale('de-CH').toFormat('EEE')}
          </li>
        ))}
      </ul>

      <RadioGroup
        value={selectedDate.toSQLDate()}
        onChange={(value) => onSelect(DateTime.fromSQL(value as string))}
        aria-label="Day select"
        className="flex flex-row justify-between"
      >
        {weekdays.map((day) => {
          // Derives the schedule for the day from the presences scheduled on it.
          const daySchedule = extendSlotsToSingleSchedule(day, buildPresenceSlots(day, presences));

          // Disable days that are in the past, salon is closed or too far in the future
          const tooFarInFuture = day.diff(DateTime.now(), 'days').days > 5;
          const disabled =
            day.diff(DateTime.now(), 'days').days < -1 || daySchedule.closed || tooFarInFuture;

          const { wrapper, text } = buttonVariants({
            fill: 'ghost',
            disabled,
          });

          return (
            <Field key={day.toSQLDate()}>
              <Radio
                value={day.toSQLDate()}
                disabled={disabled}
                className={wrapper({
                  className: 'font-aribau w-10 bg-gray-200 hover:bg-primary-200',
                })}
              >
                <span className={text({ className: 'text-sm' })}>{day.toFormat('d')}</span>
              </Radio>
            </Field>
          );
        })}
      </RadioGroup>
    </Stack>
  );
}
